import React, {ReactElement, useContext} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import SEO from '../components/seo';
import DynamicLink from '../components/DynamicLink/DynamicLink';
import LocaleContext, {Locale} from '../RootProvider/LocaleContext';
import RenderOnLocales from '../RenderOnLocales';
import GeneralLayout from '../components/DesignSystem/Layout/GeneralLayout/GeneralLayout';
import LayoutSection from '../components/DesignSystem/Layout/LayoutSection/LayoutSection';
import {LayoutBackground} from '../components/DesignSystem/Layout/LayoutSection/LayoutSection.types';
import UpdatesStrip from '../components/DesignSystem/UpdatesStrip/UpdatesStrip';
import HomepageHero from '../components/Homepage/Hero/Hero';
import LookingForFinance, {LookingForFinanceSize} from '../components/DesignSystem/LookingForFinance/LookingForFinance';
import BankingPartnerSection, {
  britishBusinessBankCircles,
  Size,
} from '../components/DesignSystem/BankingPartnerSection/BankingPartnerSection';
import HomepageHowDoesItWork from '../components/DesignSystem/HowDoesItWork/HowDoesItWork';
import howDoesItWork from '../components/DesignSystem/HowDoesItWork/homepageHowDoesItWorkContent';
import GetStartedButton from '../components/DesignSystem/Buttons/GetStarted/GetStarted';
import UpdatesBlock from '../components/DesignSystem/UpdatesBlock/UpdatesBlock';
import OurProducts from '../components/DesignSystem/OurProducts/OurProducts';
import BusinessStats from '../components/Homepage/BusinessStats/BusinessStats';
import HomepagePartners from '../components/Homepage/Partners/Partners';
import PressQuery from '../components/Homepage/Press/PressQuery';
import CustomerStoriesQuery from '../components/Homepage/CustomerStories/CustomerStoriesQuery';
import WantToKnowMore from '../components/Homepage/KnowMoreAccordion/KnowMoreAccordionWithQuery';
import useHandleAffid from '../useHandleAffid';
import getCorporationSEOSchema from '../utils/getCorporationSEOSchema';
import {Alignment} from '../types';
import updatesBlockImage from '../images/homepage/updatesBlock.png';
import GreenFinanceInitiative from '../components/Homepage/GreenFinanceInitiative/GreenFinanceInitiative';
import BusinessServicesWithQuery from '../components/Homepage/BusinessServices/BusinessServicesWithQuery';

function HomePage(): ReactElement {
  const {formatMessage} = useIntl();
  const locale = useContext(LocaleContext);
  useHandleAffid();
  const corporationSchema = JSON.stringify(getCorporationSEOSchema({locale, formatMessage}));
  const howDoesItWorkContent = howDoesItWork(formatMessage);

  return (
    <GeneralLayout>
      <SEO
        title={formatMessage({id: 'homepage.seo.metaTitle'})}
        description={formatMessage({id: 'homepage.seo.metaDescription'})}
        scripts={[
          {
            type: 'application/ld+json',
            innerHTML: corporationSchema,
          },
        ]}
      />
      <RenderOnLocales locales={[Locale.uk]}>
        <UpdatesStrip
          content={formatMessage({id: 'homepage.updatesStrip.content'})}
          moreInformationHref={formatMessage({id: 'homepage.updatesStrip.moreInformationHref'})}
          title={formatMessage({id: 'homepage.updatesStrip.title'})}
        />
      </RenderOnLocales>
      <HomepageHero />
      <LayoutSection type={LayoutBackground.WHITE} circles={britishBusinessBankCircles} slim>
        <BankingPartnerSection size={Size.MEDIUM} />
      </LayoutSection>
      <RenderOnLocales locales={[Locale.uk]}>
        <UpdatesBlock
          ctaText={formatMessage({id: 'homepage.updatesBlock.cta.text'})}
          ctaUrl={formatMessage({id: 'homepage.updatesBlock.cta.href'})}
          description={formatMessage({id: 'homepage.updatesBlock.description'})}
          image={updatesBlockImage}
          imageAlignment={Alignment.LEFT}
          subText={
            <FormattedMessage
              id="homepage.updatesBlock.subtext.text"
              values={{
                a: (...chunks: string[]) => (
                  <DynamicLink href={formatMessage({id: 'homepage.updatesBlock.subtext.href'})}>{chunks}</DynamicLink>
                ),
              }}
            />
          }
          subTitle={formatMessage({id: 'homepage.updatesBlock.subtitle'})}
          title={formatMessage({id: 'homepage.updatesBlock.title'})}
        />
      </RenderOnLocales>
      <HomepageHowDoesItWork content={howDoesItWorkContent} button={<GetStartedButton />} />
      <OurProducts />
      <BusinessStats />
      <RenderOnLocales locales={[Locale.uk]}>
        <BusinessServicesWithQuery />
      </RenderOnLocales>
      <GreenFinanceInitiative />
      <CustomerStoriesQuery />
      <HomepagePartners />
      <PressQuery />
      <LayoutSection type={LayoutBackground.WHITE}>
        <WantToKnowMore />
      </LayoutSection>
      <LayoutSection type={LayoutBackground.WHITE}>
        <LookingForFinance size={LookingForFinanceSize.LG} />
      </LayoutSection>
    </GeneralLayout>
  );
}

export default HomePage;
